import { useState, useEffect } from "react";
import { Lock, Clock, AlertCircle, CheckCircle2, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * GateLockScreen — Shown when a HOS assessment is gated (locked/expired)
 * 
 * Props:
 *  - candidateId: string
 *  - onAccessGranted: () => void — called when gate check passes
 */
export function GateLockScreen({ candidateId, onAccessGranted }) {
  const [checking, setChecking] = useState(true);
  const [gateData, setGateData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!candidateId) return;

    const checkGate = async () => {
      try {
        const res = await fetch(`${API}/ta/gate/check`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ candidate_id: candidateId }),
        });
        const data = await res.json();
        setGateData(data);

        if (data.access_allowed || !data.gate_required) {
          onAccessGranted?.();
        }
      } catch (err) {
        setError("Unable to verify access. Please try again.");
      } finally {
        setChecking(false);
      }
    };

    checkGate();
  }, [candidateId]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ShieldCheck className="w-12 h-12 text-[#00AEEF] mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Error</h2>
            <p className="text-gray-600">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Access granted — this shouldn't render (onAccessGranted handles it)
  if (gateData?.access_allowed) return null;

  const isLocked = gateData?.gate_status === "locked";
  const isExpired = gateData?.gate_status === "expired";
  const linkedTA = gateData?.linked_ta;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4" data-testid="gate-lock-screen">
      <Card className="max-w-lg w-full">
        <CardContent className="pt-8 text-center">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
            isExpired ? "bg-red-100" : "bg-amber-100"
          }`}>
            {isExpired ? (
              <Clock className="w-8 h-8 text-red-500" />
            ) : (
              <Lock className="w-8 h-8 text-amber-600" />
            )}
          </div>

          <h2 className="text-2xl font-bold text-[#1B3A5C] mb-3" data-testid="gate-lock-title">
            {isExpired ? "Access Window Expired" : "Assessment Locked"}
          </h2>

          {isLocked && (
            <>
              <p className="text-gray-600 mb-6 leading-relaxed">
                This Hands-On Simulation is locked until you complete the Technical Assessment.
                Once completed, you will be able to access this simulation.
              </p>

              {linkedTA && (
                <div className="bg-[#00AEEF]/5 border border-[#00AEEF]/20 rounded-lg p-4 mb-6 text-left">
                  <h4 className="text-sm font-semibold text-[#1B3A5C] mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#00AEEF]" />
                    Linked Technical Assessment
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Candidate</span>
                      <p className="font-medium text-[#3b3b3b]">{linkedTA.full_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Status</span>
                      <p className={`font-medium capitalize ${
                        linkedTA.status === "completed" ? "text-green-600" : "text-amber-600"
                      }`}>
                        {linkedTA.status || "Pending"}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {isExpired && (
            <p className="text-gray-600 mb-6 leading-relaxed">
              Please contact support if you cannot access your simulation.
            </p>
          )}

          <p className="text-sm text-gray-500">
            {gateData?.message}
          </p>
        </CardContent>
      </Card>

      <footer className="fixed bottom-0 w-full border-t bg-white py-4 text-center">
        <p className="text-sm text-gray-500">© 2026 THCO</p>
      </footer>
    </div>
  );
}
